import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    UserCircle2, KeyRound, Activity, ExternalLink, Save, CheckCircle2, Loader2, AlertCircle,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";

/**
 * "My Account" dialog for shop owners.
 *
 * The headline feature here is BYOK (Bring Your Own Gemini Key) — the owner
 * pastes their own Google AI Studio key so all AI image generation is billed
 * directly to their Google account (no Vastra-side credits or PhonePe needed).
 *
 * - Top: today's AI usage vs daily quota.
 * - Bottom: step-by-step "how to get a Gemini key" guide + paste + Test + Save.
 */
export default function ShopSettingsDialog() {
    const [open, setOpen] = useState(false);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const [keyInput, setKeyInput] = useState("");
    const [testing, setTesting] = useState(false);
    const [testOk, setTestOk] = useState(null);   // null | true | false
    const [testMsg, setTestMsg] = useState("");
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/owner/settings");
            setData(data);
            setKeyInput(""); setTestOk(null); setTestMsg("");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
        } finally { setLoading(false); }
    };

    useEffect(() => { if (open) load(); }, [open]);

    const testKey = async () => {
        const k = keyInput.trim();
        if (!k) { toast.error("Paste your key first"); return; }
        setTesting(true); setTestOk(null); setTestMsg("");
        try {
            const { data } = await api.post("/owner/test-gemini-key", { gemini_api_key: k });
            setTestOk(true);
            setTestMsg(data.message || "Key verified");
        } catch (e) {
            setTestOk(false);
            setTestMsg(formatApiErrorDetail(e.response?.data?.detail) || "Validation failed");
        } finally { setTesting(false); }
    };

    const saveKey = async (newKey) => {
        setSaving(true);
        try {
            await api.patch("/owner/settings", { gemini_api_key: newKey });
            toast.success(newKey ? "Your Gemini key is now active" : "Gemini key cleared — using platform key");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
        } finally { setSaving(false); }
    };

    const used = data?.usage?.today_platform ?? 0;
    const limit = data?.daily_quota ?? 100;
    const pct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button className="ayraa-btn-ghost text-sm font-mono uppercase tracking-wider-2 text-[11px]" title="My Account" data-testid="shop-settings-btn">
                    <UserCircle2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> My Account
                </button>
            </DialogTrigger>
            <DialogContent className="bg-brand-paper border-brand-border max-w-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <div className="ayraa-overline">Vastra · My Account</div>
                    <DialogTitle className="font-display text-3xl tracking-editorial mt-1">
                        {data?.shop_name || "Your boutique"}
                    </DialogTitle>
                </DialogHeader>

                {loading || !data ? (
                    <div className="space-y-4 py-6">
                        <div className="h-24 skel rounded-sm" />
                        <div className="h-48 skel rounded-sm" />
                    </div>
                ) : (
                    <div className="space-y-8" data-testid="shop-settings-body">
                        {/* ───── Today's usage ───── */}
                        <section>
                            <div className="flex items-center gap-2 mb-3">
                                <Activity className="w-4 h-4 text-brand-bronze" strokeWidth={1.5} />
                                <h3 className="font-display text-xl tracking-editorial">Today&apos;s AI usage</h3>
                            </div>
                            <div className="border border-brand-border bg-brand-paper p-5 rounded-sm" data-testid="usage-card">
                                <div className="flex items-baseline justify-between">
                                    <div>
                                        <div className="font-display text-4xl tracking-editorial" data-testid="usage-used">
                                            <span className="font-mono">{data.has_byok_key ? data.usage.today_byok : used}</span>
                                        </div>
                                        <div className="ayraa-overline mt-1">
                                            {data.has_byok_key ? "On your Gemini key" : "Platform try-ons today"}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        {data.has_byok_key ? (
                                            <div className="font-mono text-xs text-emerald-700 uppercase tracking-wider-2">
                                                Unlimited · billed to Google
                                            </div>
                                        ) : (
                                            <div className="font-mono text-sm">
                                                <span className="font-medium">{limit}</span>
                                                <span className="text-brand-soft"> / day cap</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!data.has_byok_key ? (
                                    <div className="mt-4 h-1.5 bg-brand-border overflow-hidden">
                                        <div
                                            className="h-full bg-brand-bronze transition-all duration-500"
                                            style={{ width: `${pct}%` }}
                                            data-testid="usage-bar"
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </section>

                        {/* ───── BYOK ───── */}
                        <section>
                            <div className="flex items-center gap-2 mb-3">
                                <KeyRound className="w-4 h-4 text-brand-bronze" strokeWidth={1.5} />
                                <h3 className="font-display text-xl tracking-editorial">Your Gemini API key</h3>
                            </div>
                            <p className="text-brand-muted text-sm leading-relaxed mb-5 max-w-prose text-pretty">
                                Add your own Google Gemini key and every AI try-on, model render and reel is
                                billed directly to your Google account at Google&apos;s own rate (~₹3 / image).
                                No more daily caps, no credits to manage, no middleman.
                            </p>

                            {data.has_byok_key ? (
                                /* ─── Already configured ─── */
                                <div className="border border-emerald-200 bg-emerald-50/60 rounded-sm p-5 flex items-center justify-between gap-3" data-testid="byok-current">
                                    <div className="min-w-0 flex items-center gap-3">
                                        <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0" strokeWidth={1.5} />
                                        <div className="min-w-0">
                                            <div className="ayraa-overline text-emerald-800">Active</div>
                                            <div className="font-mono text-sm mt-0.5 truncate" data-testid="byok-mask">{data.gemini_api_key_masked}</div>
                                        </div>
                                    </div>
                                    <button
                                        className="ayraa-btn-outline text-xs uppercase tracking-wider-2 font-mono py-2 px-4 shrink-0"
                                        onClick={() => { if (window.confirm("Remove your Gemini key? Future try-ons will use the shared platform key (subject to the daily quota).")) saveKey(""); }}
                                        disabled={saving}
                                        data-testid="byok-clear-btn"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                /* ─── Step-by-step onboarding + paste form ─── */
                                <div className="border border-brand-border bg-brand-paper rounded-sm p-5 space-y-5" data-testid="byok-card">
                                    {/* 3-step instructions */}
                                    <ol className="space-y-3 text-sm">
                                        <li className="flex gap-3">
                                            <span className="font-mono text-xs tracking-wider-2 bg-brand-ink text-white w-6 h-6 grid place-items-center shrink-0 rounded-sm mt-0.5">01</span>
                                            <div className="text-brand-ink">
                                                Open
                                                <a
                                                    href="https://aistudio.google.com/apikey"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="editorial-link mx-1.5"
                                                    data-testid="byok-get-key-link"
                                                >
                                                    aistudio.google.com/apikey
                                                    <ExternalLink className="inline w-3 h-3 ml-1" strokeWidth={1.5} />
                                                </a>
                                                and sign in with your Google account.
                                            </div>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="font-mono text-xs tracking-wider-2 bg-brand-ink text-white w-6 h-6 grid place-items-center shrink-0 rounded-sm mt-0.5">02</span>
                                            <div className="text-brand-ink">
                                                Click <span className="font-mono text-xs bg-brand-subtle px-1.5 py-0.5 rounded-sm">Create API key</span> → pick (or create) a Google Cloud project → enable billing on it (it stays free up to your monthly budget).
                                            </div>
                                        </li>
                                        <li className="flex gap-3">
                                            <span className="font-mono text-xs tracking-wider-2 bg-brand-ink text-white w-6 h-6 grid place-items-center shrink-0 rounded-sm mt-0.5">03</span>
                                            <div className="text-brand-ink">
                                                Copy the key (starts with <code className="font-mono text-xs">AIza…</code>), paste it below, hit <span className="font-mono text-xs">Test</span>, then <span className="font-mono text-xs">Save</span>.
                                            </div>
                                        </li>
                                    </ol>

                                    <div className="h-px bg-brand-border" />

                                    <div className="space-y-3">
                                        <label className="ayraa-overline">Paste your key</label>
                                        <input
                                            type="text"
                                            className="ayraa-input font-mono text-sm"
                                            placeholder="AIzaSy…"
                                            value={keyInput}
                                            onChange={(e) => { setKeyInput(e.target.value); setTestOk(null); setTestMsg(""); }}
                                            spellCheck={false}
                                            autoComplete="off"
                                            data-testid="byok-input"
                                        />

                                        {/* test result banner */}
                                        {testOk === true ? (
                                            <div className="flex items-center gap-2 text-emerald-800 bg-emerald-50/60 border border-emerald-200 rounded-sm px-3 py-2 text-sm">
                                                <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
                                                <span>{testMsg || "Key verified successfully"}</span>
                                            </div>
                                        ) : testOk === false ? (
                                            <div className="flex items-start gap-2 text-red-800 bg-red-50/60 border border-red-200 rounded-sm px-3 py-2 text-sm">
                                                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                                                <span>{testMsg}</span>
                                            </div>
                                        ) : null}

                                        <div className="grid grid-cols-2 gap-2 pt-1">
                                            <button
                                                className="ayraa-btn-outline w-full py-3"
                                                onClick={testKey}
                                                disabled={testing || !keyInput.trim()}
                                                data-testid="byok-test-btn"
                                            >
                                                {testing ? (
                                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Testing…</>
                                                ) : (
                                                    <>Test key</>
                                                )}
                                            </button>
                                            <button
                                                className="ayraa-btn-primary w-full py-3"
                                                onClick={() => saveKey(keyInput.trim())}
                                                disabled={saving || !keyInput.trim() || testOk === false}
                                                data-testid="byok-save-btn"
                                            >
                                                {saving ? (
                                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
                                                ) : (
                                                    <><Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> Save key</>
                                                )}
                                            </button>
                                        </div>

                                        <p className="text-xs text-brand-soft text-center">
                                            Stored securely on Vastra&apos;s servers and never displayed back in full.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
